import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@mui/material'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomDialog from 'components/dialogs/CustomDialog'
import { useProfileContext } from 'context/ProfileContext'
import { useAddExperience, useGetExperienceById, useUpdateExperience } from 'hooks/useUsers'
import { FormProvider, useForm } from 'react-hook-form'
import { snackbarService } from 'services/snackbarService'
import * as yup from 'yup';
import { Dayjs } from 'dayjs';
import dayjs from 'lib/dayjs';
import { useAuth } from 'context/AuthContext'
import PlacesSearch from 'components/common/PlacesSearch/PlacesSearch'
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect'
import CustomDatePicker from 'components/inputs/CustomDatePicker/CustomDatePicker'
import { useGetSpecialities } from 'hooks/useLookups'
import { toTitleCase } from 'common/utils'


interface Props {
  open: boolean,
  setOpen: (value: boolean) => void;
  positionId?: number;
  entity: string;
  refetchHospitals?: () => void;
}

export default function PositionDialog({
  open,
  setOpen,
  positionId,
  entity,
  refetchHospitals,
}: Props) {
  const { user } = useAuth();
  const { refetch: refetchProfile } = useProfileContext();
  const { data: specialities } = useGetSpecialities();

  const getValidationSchema = () => {
    const baseSchema = {
      start_date: yup
        .mixed<Dayjs>()
        .nullable()
        .required('Start date is required'),
      hospital: yup.object({
        name: yup.string().required(),
        address: yup.string().required(),
      }).required('Institution is required'),
      hospital_name: yup.string().required('Institution is required'),
      speciality_id: yup.string().required('Speciality is required'),
    };

    if (entity === 'experience') {
      return yup.object({
        ...baseSchema,
        end_date: yup
          .mixed<Dayjs>()
          .nullable()
          .required('End date is required')
          .test(
            'is-after-start',
            'End date must be after start date',
            function (value) {
              const { start_date } = this.parent;
              if (!value || !start_date) return true;
              return value.isAfter(start_date);
            }
          ),
      });
    }

    return yup.object({
      ...baseSchema,
      end_date: yup
        .mixed<Dayjs>()
        .nullable()
        .optional(),
    });
  };

  const validationSchema = getValidationSchema();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      start_date: undefined,
      end_date: undefined,
      hospital_name: '',
      hospital: {
        name: '',
        address: '',
      },
      speciality_id: '',
    }
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
    setValue,
  } = methods;

  useGetExperienceById({
    userId: user.id,
    experienceId: positionId as number,
  }, {
    enabled: !!positionId,
    onSuccess: (data: any) => {

      const startDate = data.start_date ? dayjs(data.start_date.toISOString().split('T')[0]) : null;
      const endDate = data.end_date ? dayjs(data.end_date.toISOString().split('T')[0]) : null;

      reset({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        hospital: {
          name: data.hospital?.name || '',
          address: data.hospital?.name || '',
        },
        hospital_name: data.hospital.name || '',
        speciality_id: data.speciality.id.toString() || '',
      });
    },
    onError: (error: any) => {
      console.log('error', error);
    }
  })

  const { mutate: createMutation, isLoading: isCreating } = useAddExperience(
    (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode === 200) {
        setOpen(false);
        snackbarService.showSnackbar(`${toTitleCase(entity)} has been added successfully`, 'success');
        refetchProfile();
        refetchHospitals?.();
        resetForm();
      } else {
        snackbarService.showSnackbar(data, 'error');
      }
    },
    (error: any) => { console.log('error', error) },
  )

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateExperience(
    (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode === 200) {
        setOpen(false);
        snackbarService.showSnackbar(`${toTitleCase(entity)} has been updated successfully`, 'success');
        refetchProfile();
      } else {
        snackbarService.showSnackbar(data, 'error');
      }
    },
    (error: any) => { console.log('error', error) },
  )

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      start_date: data.start_date
        ? dayjs(data.start_date).format('MMM DD, YYYY')
        : null,
    };

    if (positionId) {
      updateMutation({
        userId: user.id,
        experienceId: positionId,
        dto: payload,
      });
    } else {
      createMutation({
        userId: user.id,
        dto: payload,
      });
    }
  };

  const resetForm = () => {
    reset({
      start_date: undefined,
      end_date: undefined,
      hospital: {
        name: '',
        address: '',
      },
      speciality_id: '',
    });
  }

  return (
    <CustomDialog
      open={open}
      title={`${positionId ? 'Edit' : 'Add'} ${toTitleCase(entity)}`}
      alignText='center'
      onClose={() => setOpen(false)}
      content={
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
          >

            <Box mt={3}>
              <PlacesSearch
                name="hospital_name"
                control={control}
                onChange={(name) => {
                  setValue(
                    "hospital",
                    { name, address: name },
                    { shouldDirty: true, shouldValidate: true }
                  );
                }}
              />
            </Box>

            <Box mt={2}>
              <CustomFormSelect
                name={'speciality_id'}
                options={specialities || []}
                floatingLabel='Speciality'
                placeholder='Select speciality'
                required
              />
            </Box>

            <Box mt={2}>
              <CustomDatePicker
                name={'start_date'}
                placeholder='Start date'
                control={control}
                floatingLabel='Start date'
                required
              />
            </Box>

            {entity == 'experience' && <Box mt={2}>
              <CustomDatePicker
                name={'end_date'}
                placeholder='End date'
                control={control}
                floatingLabel='End date'
                required
              />
            </Box>}

            <Box my={2}>
              <CustomButton
                text={`${positionId ? 'Save' : 'Add'}`}
                color='primary'
                fullWidth
                type='submit'
                size='large'
                loading={isCreating || isUpdating}
                disabled={!isValid}
              />
            </Box>

          </Box>
        </FormProvider>
      }
    />
  )
}
